import { components, paths } from "@/api-schema";
import {
  PathsV1ModelGenerationSearchPostParametersQueryExtend,
  PathsV1ModelGenerationSearchPostParametersQueryOrderByDirection,
  PathsV1ModelGenerationSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useModelGenerationDeleteMutation } from "@/common/api/model-generation/use-model-generation-delete.mutation";
import { useModelGenerationSearchQuery } from "@/common/api/model-generation/use-model-generation-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures, TimestampSortField } from "@/components/data-table/types";
import { BrandFilter } from "@/components/filter/predefined/brand-filter";
import { ModelFamilyFilter } from "@/components/filter/predefined/model-family-filter";
import { ModelGenerationDatIdFilter } from "@/components/filter/predefined/model-generation-dat-id-filter";
import { VehicleKindFilter } from "@/components/filter/predefined/vehicle-kind-filter";
import { RowActionVariant } from "@/components/ui/table-row-actions";
import i18next from "i18next";
import { GitMerge } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { modelGenerationTableDefaultColumns } from "./model-generation-table-columns";

type ModelGeneration = components["schemas"]["ModelGeneration"] & RowWithIdentifier;
type ModelGenerationSorting = NonNullable<
  paths["/v1/model-generation/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type ModelGenerationQueryParams = paths["/v1/model-generation/search"]["post"]["parameters"]["query"];
type ModelGenerationRequestBody =
  paths["/v1/model-generation/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  modelGenerationTableDefaultColumns.columnName,
  modelGenerationTableDefaultColumns.columnModelFamily,
  modelGenerationTableDefaultColumns.columnBrand,
  modelGenerationTableDefaultColumns.columnVehicleKind,
  modelGenerationTableDefaultColumns.columnDatId,
];

const MODEL_GENERATION_TABLE_CONFIG: PredefinedTableConfig<ModelGenerationSorting, "brand"> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1ModelGenerationSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1ModelGenerationSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchModels"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: false,
  } as const,
  sortingMapping: {
    name: ["name"],
    modelFamily: ["modelFamily"],
    brand: ["brandName"],
    vehicleKind: ["vehicleKind"],
    datId: ["datId"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  sortColumns: [
    { id: PathsV1ModelGenerationSearchPostParametersQueryOrderByField.Name, label: () => i18next.t("common:Name") },
    {
      id: PathsV1ModelGenerationSearchPostParametersQueryOrderByField.ModelFamily,
      label: () => i18next.t("masterdata:ModelFamily"),
    },
    { id: "brand", label: () => i18next.t("common:Brand") },
    {
      id: PathsV1ModelGenerationSearchPostParametersQueryOrderByField.VehicleKind,
      label: () => i18next.t("masterdata:VehicleKind"),
    },
    {
      id: PathsV1ModelGenerationSearchPostParametersQueryOrderByField.DatId,
      label: () => i18next.t("masterdata:DATID"),
    },
  ],
  timestampSortFields: [TimestampSortField.CreatedAt, TimestampSortField.UpdatedAt],
} as const;

const MODEL_GENERATION_TABLE_QUERY_HOOKS: DataFetchHooks<
  ModelGeneration,
  ModelGenerationQueryParams,
  ModelGenerationRequestBody
> = {
  useSearchQuery: useModelGenerationSearchQuery,
} as const;

const DEFAULT_FILTER_VALUES: Record<string, string[]> = {
  brand: [],
  modelFamily: [],
  identifier: [],
  vehicleKind: [],
};

export interface ModelGenerationTableProps extends BasePredefinedTableProps<
  ModelGeneration,
  ModelGenerationSorting,
  ModelGenerationQueryParams,
  ModelGenerationRequestBody
> {
  onMerge?: (modelGeneration: ModelGeneration) => void;
}

const BaseModelGenerationTable = createBasePredefinedTable({
  config: MODEL_GENERATION_TABLE_CONFIG,
  queryHooks: MODEL_GENERATION_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function ModelGenerationTable(props: ModelGenerationTableProps) {
  const { t } = useTranslation(["common", "masterdata", "ram"]);
  const [_, navigate] = useLocation();
  const { name = "model-generation-table", showCreateButton = true, onMerge, ...restProps } = props;
  const deleteMutation = useModelGenerationDeleteMutation();
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>(DEFAULT_FILTER_VALUES);

  const filters = useMemo(
    () => [
      new ModelGenerationDatIdFilter({
        body: {
          brand: filterValues.brand ?? [],
          modelFamily: filterValues.modelFamily ?? [],
        },
      }),
      new BrandFilter({ id: "brand" }),
      new ModelFamilyFilter({
        id: "modelFamily",
        body: { brand: filterValues.brand ?? [] },
      }),
      new VehicleKindFilter(),
    ],
    [filterValues.brand, filterValues.modelFamily],
  );

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    setFilterValues((currentValues) => ({
      ...currentValues,
      [filterId]: values,
      ...(filterId === "brand" ? { modelFamily: [], identifier: [] } : {}),
      ...(filterId === "modelFamily" ? { identifier: [] } : {}),
    }));
  }, []);

  const handleEdit = useCallback(
    (modelGeneration: ModelGeneration) => {
      navigate(`~/masterdata/model-generation/${modelGeneration.uuid}/update`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (modelGeneration: ModelGeneration) => {
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: modelGeneration.uuid }),
        successMessage: t("masterdata:ModelSuccessfullyDeleted", { name: modelGeneration.name }),
        errorMessage: t("masterdata:ErrorDeletingModel", { name: modelGeneration.name }),
      });
    },
    [deleteMutation, t],
  );

  const customRowActions = useCallback(
    (row: { original: ModelGeneration }) => {
      if (!onMerge) return [];

      return [
        {
          label: t("masterdata:Merge"),
          icon: <GitMerge className="h-4 w-4" />,
          variant: RowActionVariant.Warning,
          onClick: () => onMerge(row.original),
        },
      ];
    },
    [onMerge, t],
  );

  return (
    <BaseModelGenerationTable
      searchPlaceholder={t("common:SearchModels")}
      extend={[
        PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily,
        PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelFamily_Brand,
        PathsV1ModelGenerationSearchPostParametersQueryExtend.ModelGenerationIdentifiers,
      ]}
      {...{
        name,
        showCreateButton,
        createButtonLink: "~/masterdata/model-generation/create",
        onEdit: handleEdit,
        onDelete: handleDelete,
        customRowActions,
        confirmActions: ["delete"],
        confirmLabels: {
          delete: {
            title: t("masterdata:DeleteModel"),
            text: t("masterdata:DeleteModelDescription"),
            confirmButton: t("ram:YesDelete"),
            cancelButton: t("common:Cancel"),
          },
        },
        filters,
        filterValues,
        onFilterChange: handleFilterChange,
        ...restProps,
      }}
    />
  );
}
